import React from 'react';
import { Alert, Box, Chip } from '@mui/material';
import { FormDialog } from '../../../../../components/form-dialog';
import { ServiceProviderEventFormFields } from '../service-provider-event-form-fields';
import { useServiceProviderEventFormState } from './use-service-provider-event-form-state';
import { EventDayStrip } from '../event-day-strip';
import { combineDateAndTime, extractDateAndTime } from '../../../../../utilities';
import { RecurrenceEditor } from '../../../../../components/calendar/recurrence/recurrence-editor';
import type { RecurrencePattern } from '../../../../../components/calendar/recurrence/recurrence-pattern';

export interface CreateEventDialogSubmitValues {
  title: string;
  locationAddress: string;
  responsibleUserId: string;
  startAt: string;
  endAt: string;
  allDay: boolean;
  content?: string;
  partialObjectiveId?: string;
  observationTypeId?: string;
  recurrence?: RecurrencePattern;
  noteTypeId?: string;
}

interface CreateEventDialogProps {
  initialStartAt: Date;
  initialEndAt: Date;
  initialResponsibleUserId?: string;
  partialObjective?: { id: string; title: string; description?: string };
  observationType?: { id: string; name: string };
  onClose: () => void;
  onSubmit: (values: CreateEventDialogSubmitValues) => Promise<void>;
}

export function CreateEventDialog(props: CreateEventDialogProps) {
  const {
    initialStartAt,
    initialEndAt,
    initialResponsibleUserId,
    partialObjective,
    observationType,
    onClose,
    onSubmit,
  } = props;
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [recurrence, setRecurrence] = React.useState<RecurrencePattern | undefined>(undefined);
  const {
    formState,
    validationErrors,
    setField,
    onAllDayChange,
    onStartDateChange,
    setStartDateValidationError,
    setStartTimeValidationError,
    setEndDateValidationError,
    setEndTimeValidationError,
    buildSubmitValues,
  } = useServiceProviderEventFormState({
    title: partialObjective?.title ?? observationType?.name ?? '',
    locationAddress: '',
    responsibleUserId: initialResponsibleUserId,
    allDay: false,
    startAt: initialStartAt,
    endAt: initialEndAt,
    content: partialObjective?.description ?? '',
    noteTypeId: undefined,
  });

  const showStrip = !!partialObjective;
  const stripStartAt =
    combineDateAndTime(formState.startDate, formState.startTime) ?? initialStartAt;
  const stripEndAt =
    combineDateAndTime(formState.endDate, formState.endTime) ?? initialEndAt;

  function handleClose() {
    if (isSubmitting) {
      return;
    }

    onClose();
  }

  async function handleSubmit() {
    const submitValuesResult = buildSubmitValues();
    if (!submitValuesResult.values) {
      setError(submitValuesResult.error ?? null);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        ...submitValuesResult.values,
        partialObjectiveId: partialObjective?.id,
        observationTypeId: observationType?.id,
        recurrence,
      });
    } catch {
      setError('Kunne ikke oprette begivenhed. Prøv igen.');
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleSlotSelect(start: Date, end: Date, nextAllDay: boolean) {
    const startParts = extractDateAndTime(start);
    const endParts = extractDateAndTime(end);
    if (startParts) {
      setField('startDate', startParts.date);
      setField('startTime', startParts.time);
    }
    if (endParts) {
      setField('endDate', endParts.date);
      setField('endTime', endParts.time);
    }
    if (nextAllDay !== formState.allDay) {
      onAllDayChange(nextAllDay);
    }
  }

  const formFields = (
    <>
      <ServiceProviderEventFormFields
        errors={validationErrors}
        title={formState.title}
        onTitleChange={value => setField('title', value)}
        locationAddress={formState.locationAddress}
        onLocationAddressChange={value => setField('locationAddress', value)}
        responsibleUserId={formState.responsibleUserId}
        onResponsibleUserIdChange={value => setField('responsibleUserId', value)}
        allDay={formState.allDay}
        onAllDayChange={onAllDayChange}
        startDate={formState.startDate}
        onStartDateChange={onStartDateChange}
        onStartDateValidation={setStartDateValidationError}
        startTime={formState.startTime}
        onStartTimeChange={value => setField('startTime', value)}
        onStartTimeValidation={setStartTimeValidationError}
        endDate={formState.endDate}
        onEndDateChange={value => setField('endDate', value)}
        onEndDateValidation={setEndDateValidationError}
        endTime={formState.endTime}
        onEndTimeChange={value => setField('endTime', value)}
        onEndTimeValidation={setEndTimeValidationError}
        content={formState.content}
        onContentChange={value => setField('content', value)}
        noteTypeId={formState.noteTypeId}
        onNoteTypeIdChange={value => setField('noteTypeId', value)}
      />
      <Box sx={{ mt: 2 }}>
        <RecurrenceEditor value={recurrence} onChange={setRecurrence} />
      </Box>
    </>
  );

  return (
    <FormDialog
      open
      title="Opret begivenhed"
      disableBackdropClick
      confirmButtonText="Opret"
      cancelButtonText="Afbryd"
      onClose={handleClose}
      onCancelClick={handleClose}
      onConfirmClick={handleSubmit}
      isLoading={isSubmitting}
      fullWidth
      maxWidth={showStrip ? 'lg' : 'sm'}
    >
      {partialObjective && (
        <Chip
          label={`Delmål: ${partialObjective.title}`}
          size="small"
          sx={{ mb: 1, mt: 1 }}
        />
      )}
      {observationType && (
        <Chip
          label={`Observationstype: ${observationType.name}`}
          size="small"
          sx={{ mb: 1, mt: 1 }}
        />
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
      {showStrip ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            mt: 1,
          }}
        >
          <Box sx={{ flex: { md: '0 0 420px' } }}>{formFields}</Box>
          <Box sx={{ flex: 1, minHeight: 520 }}>
            <EventDayStrip
              startAt={stripStartAt}
              endAt={stripEndAt}
              allDay={formState.allDay}
              draftTitle={formState.title || partialObjective.title}
              onSlotSelect={handleSlotSelect}
            />
          </Box>
        </Box>
      ) : (
        formFields
      )}
    </FormDialog>
  );
}
